import { createSlice } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import {
  fetchCollections,
  fetchCollection,
  fetchDataModels,
  fetchBackgroundTasks,
  deleteDataModel,
  createDataModel,
  deleteCollection,
  updateCollection,
  createCollection,
  updateDataModel,
  createBackgroundTask,
  deleteBackgroundTask,
  fetchDataModel,
  fetchAllRecordsFromTable,
  updateBackgroundTask,
  createService,
  fetchEndpoints,
  fetchServices,
  fetchService,
  fetchSwaggerData,
  deleteEndpoint,
  deleteService,
  fetchDataModelRegions,
  updateService,
  fetchPipelineStatus,
  fetchReferenceCollectionsData,
  updateEndpoint,
  fetchENVSecrets,
  createSecret,
  deleteSecret,
  updateSecretKey,
  fetchDataModelDeploymentsTrack,
  addNewRecordIntoTable,
  fetchFilteredDataModels,
  deployBEApp,
} from "./dataModels.actions";
import {
  EMPTY_STRING,
  BACKEND_FAILURE_STATUS,
  BACKEND_INITIAL_STATUS,
  DEFAULT_LIMIT,
  ZERO,
  ONE,
  AUTO_MAPPER_OPERATIONS,
  EMPTY_OBJECT,
  EMPTY_ARRAY,
} from "../../constants";

const {
  ADD,
  EDIT,
  UPDATE,
  DELETE,
  RESET_MAPPER,
  POPULATE_MAPPER,
  MODIFY_RESPONSE,
  MODIFY_SOURCE,
  EDGES,
  NODES,
  SET_RESPONSE_EXPANDED_KEYS,
  SET_SOURCE_EXPANDED_KEYS,
  SHOULD_SHOW_RESPONSE_EDITOR,
  SHOULD_SHOW_SOURCE_EDITOR,
  RESET_AUTO_MAPPER,
} = AUTO_MAPPER_OPERATIONS;

const paginationFilters = {
  name: EMPTY_STRING,
  page: ONE,
  limit: DEFAULT_LIMIT,
};

const searchFilter = {
  name: EMPTY_STRING,
};

const defaultFilter = [];

const initialState = {
  dataModels: {
    data: [],
  },
  collections: {
    data: [],
  },
  tableData: {
    data: [],
  },
  collection: null,
  selectedProperty: null,
  selectedDataModelId: null,
  selectedCollectionId: null,
  backgroundTasks: {
    data: [],
  },
  endpoints: [],
  services: [],
  selectedBackgroundTask: null,
  selectedDataModel: null,
  selectedDataTable: null,
  selectedRecords: [],
  totalCollections: ZERO,
  totalBackgroundTasks: ZERO,
  totalDataModels: ZERO,
  dataModelsFilters: paginationFilters,
  collectionsFilters: searchFilter,
  tableDataFilters: defaultFilter,
  formattedFilters: null,
  backgroundTaskFilters: searchFilter,
  endpointFilters: searchFilter,
  serviceFilters: searchFilter,
  totalEndpoints: ZERO,
  totalServices: ZERO,
  service: null,
  selectedEndpoint: null,
  selectedServiceId: null,
  selectedNode: null,
  shouldShowLoadingPage: false,
  nodeDetails: null,
  swaggerData: null,
  dataModelRegions: null,
  deployStatus: null,
  pipelineStatus: {},
  referenceCollectionsData: [],
  recentDeployedEndpointInfo: null,
  secrets: [],
  selectedSecretKey: null,
  dataModelDeploymentsTrack: { deploymentStatus: [], count: null },
  autoMapper: {
    sourceObject: {},
    responseObject: {},
    mapperObject: {},
    nodes: [],
    edges: [],
    shouldShowSourceEditor: false,
    shouldShowResponseEditor: false,
    expandedKeys: {
      source: [],
      response: [],
    },
  },
  newService: null,
  dataModelResources: EMPTY_OBJECT,
  areResourcesPending: false,
  dataModelWithPendingResources: null,
  filteredDataModels:EMPTY_ARRAY
};

const slice = createSlice({
  name: "dataModels",
  initialState,
  reducers: {
    setSelectedProperty(state, action) {
      state.selectedProperty = action.payload;
    },
    setSelectedDataModelId(state, action) {
      state.selectedDataModelId = action.payload;
    },
    setCollection(state, action) {
      state.collection = action.payload;
    },

    updateTableData(state, action) {
      state.tableData = {
        ...state.tableData,
        data: [...state.tableData.data, action.payload],
      };
    },
    setSelectedCollectionId(state, action) {
      state.selectedCollectionId = action.payload;
    },
    setSelectedDataTable(state, action) {
      state.selectedDataTable = action.payload;
    },
    setSelectedRecords(state, action) {
      state.selectedRecords = action.payload;
    },
    resetSelectedProperty(state) {
      state.selectedRecords = [];
    },
    resetImageSelector(state, action) {
      state.selectedProperty = action.payload;
    },
    resetTableData(state) {
      state.tableData = initialState.tableData;
    },
    setSelectedDataModel(state, action) {
      state.selectedDataModel = action.payload;
      if (action.payload) {
        state.totalBackgroundTasks = action.payload.backgroundTasks.length;
        state.totalCollections = action.payload.collections.length;
      }
    },
    setSelectedServiceId(state, action) {
      state.selectedServiceId = action.payload;
    },
    setSelectedEndpoint(state, action) {
      state.selectedEndpoint = action.payload;
    },
    setSelectedBackgroundTask(state, action) {
      state.selectedBackgroundTask = action.payload;
    },
    setDataModelsFilters(state, action) {
      state.dataModelsFilters = action.payload || paginationFilters;
    },
    setCollectionsFilters(state, action) {
      state.collectionsFilters = action.payload || searchFilter;
    },
    setTableDataFilters(state, action) {
      state.tableDataFilters = action.payload || defaultFilter;
    },
    setFormattedFilters(state, action) {
      state.formattedFilters = action.payload;
    },
    setBackgroundTaskFilters(state, action) {
      state.backgroundTaskFilters = action.payload || searchFilter;
    },
    setEndpointFilters(state, action) {
      state.endpointFilters = action.payload || searchFilter;
    },
    setServiceFilters(state, action) {
      state.serviceFilters = action.payload || searchFilter;
    },
    setReactFlowStore(state, action) {
      state.reactFlowStore = action.payload;
    },
    setSelectedNode(state, action) {
      state.selectedNode = action.payload;
    },
    setShouldShowLoadingPage(state, action) {
      state.shouldShowLoadingPage = action.payload;
    },
    setNodeDetails(state, action) {
      state.nodeDetails = action.payload;
    },
    setSelectedService(state, action) {
      state.service = action.payload;
    },
    setDeployStatus(state, action) {
      state.deployStatus = action.payload;
    },
    setPipelineStatus(state, action) {
      state.pipelineStatus = action.payload || initialState.pipelineStatus;
    },
    resetReferenceCollectionsData(state) {
      state.referenceCollectionsData = [];
    },
    setSelectedSecretKey(state, action) {
      state.selectedSecretKey = action.payload;
    },
    setAutoMapper(state, action) {
      const {
        key,
        value,
        operation,
        response = {},
        source = {},
        mapper = {},
        nodes = [],
        edges = [],
        sourceExpandedKeys = [],
        responseExpandedKeys = [],
        shouldShowModal,
      } = action.payload;
      const { mapperObject } = state.autoMapper;

      switch (operation) {
        case ADD:
          mapperObject[key] = value;
          break;
        case EDIT:
          if (key in mapperObject) mapperObject[key] = value;
          break;
        case UPDATE:
          mapperObject[key] = value;
          break;
        case DELETE:
          if (key in mapperObject) delete mapperObject[key];
          break;
        case RESET_MAPPER:
          state.autoMapper.mapperObject = {};
          break;
        case POPULATE_MAPPER:
          state.autoMapper.mapperObject = { ...mapper };
          break;
        case MODIFY_RESPONSE:
          state.autoMapper.responseObject = { ...response };
          break;
        case MODIFY_SOURCE:
          state.autoMapper.sourceObject = { ...source };
          break;
        case EDGES:
          state.autoMapper.edges = edges;
          break;
        case NODES:
          state.autoMapper.nodes = nodes;
          break;
        case SHOULD_SHOW_SOURCE_EDITOR:
          state.autoMapper.shouldShowSourceEditor = shouldShowModal;
          break;
        case SHOULD_SHOW_RESPONSE_EDITOR:
          state.autoMapper.shouldShowResponseEditor = shouldShowModal;
          break;
        case SET_SOURCE_EXPANDED_KEYS:
          state.autoMapper.expandedKeys.source = sourceExpandedKeys;
          break;
        case SET_RESPONSE_EXPANDED_KEYS:
          state.autoMapper.expandedKeys.response = responseExpandedKeys;
          break;
        case RESET_AUTO_MAPPER:
          state.autoMapper = {
            sourceObject: {},
            responseObject: {},
            mapperObject: {},
            nodes: [],
            edges: [],
            shouldShowSourceEditor: false,
            shouldShowResponseEditor: false,
            expandedKeys: {
              source: [],
              response: [],
            },
          };
          break;
        default:
          return;
      }
    },
    resetDataModelDetails(state) {
      state.collections.data = [];
      state.endpoints = [];
      state.services = [];
      state.backgroundTasks.data = [];
    },
    setNewService(state, action) {
      state.newService = action.payload;
    },
    setDataModelResources(state, action) {
      state.dataModelResources = action.payload;
    },
    setAreResourcesPending(state, action) {
      state.areResourcesPending = action.payload;
    },
    setDataModelWithPendingResources(state, action) {
      state.dataModelWithPendingResources = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchCollections.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.collections = action.payload;
        state.totalCollections = action.payload.total;
      })
      .addCase(fetchAllRecordsFromTable.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.tableData = action.payload;
        const updatedCollections = state.collections.data.map((collection) =>
          collection.id === state.selectedDataTable.id
            ? { ...collection, rows: state.tableData.total }
            : collection
        );
        state.collections.data = updatedCollections;
      })
      .addCase(addNewRecordIntoTable.fulfilled, (state) => {
        state.tableData.total = state.tableData.total + ONE;
      })
      .addCase(fetchCollection.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.collection = action.payload;
      })
      .addCase(fetchDataModels.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.dataModels = action.payload;
        state.totalDataModels = action.payload.total;
      })
      .addCase(fetchBackgroundTasks.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.backgroundTasks = action.payload;
        state.totalBackgroundTasks = action.payload.total;
      })
      .addCase(fetchEndpoints.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.endpoints = action.payload.data;
        state.totalEndpoints = action.payload.total;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.services = action.payload.data;
        state.totalServices = action.payload.total;
      })
      .addCase(fetchService.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.service = action.payload;
      })
      .addCase(createService.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.services = [...state.services, action.payload];
        state.totalServices = state.totalServices + 1;
      })
      .addCase(fetchDataModelRegions.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.dataModelRegions = action.payload;
      })
      .addCase(updateDataModel.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedDataModels = state.dataModels.data.map((dataModel) =>
          dataModel.id === action.payload.id
            ? { ...dataModel, ...action.payload }
            : dataModel
        );
        state.dataModels = {
          ...state.dataModels,
          data: updatedDataModels,
        };
        state.selectedDataModel = updatedDataModels.find(
          (each) => each.id === action.payload.id
        );
      })
      .addCase(deleteDataModel.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedDataModels = state.dataModels.data.filter(
          (item) => item.id !== action.payload.id
        );
        state.dataModels = {
          ...state.dataModels,
          total: state.dataModels.total - 1,
          data: updatedDataModels,
        };
        state.totalDataModels = state.totalDataModels - 1;
        const targetPage =
          state.totalDataModels % DEFAULT_LIMIT === 0
            ? state.dataModelsFilters.page - 1
            : state.dataModelsFilters.page;
        state.dataModelsFilters = {
          ...state.dataModelsFilters,
          page: targetPage,
        };
      })
      .addCase(createDataModel.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.dataModels = {
          ...state.dataModels,
          total: state.dataModels.total + 1,
          data: [...state.dataModels.data, action.payload],
        };
        state.totalDataModels = state.totalDataModels + 1;
        state.dataModelsFilters = {
          ...state.dataModelsFilters,
          page: ONE,
        };
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.collections = {
          ...state.collections,
          total: state.collections.total + 1,
          data: [...state.collections.data, { ...action.payload, rows: ZERO }],
        };
        state.totalCollections = state.totalCollections + 1;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedCollection = state.collections.data.filter(
          (item) => item.id !== action.payload.id
        );
        state.collections = {
          ...state.collections,
          total: state.collections.total - 1,
          data: updatedCollection,
        };
        state.totalCollections = state.totalCollections - 1;
      })
      .addCase(deleteEndpoint.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.endpoints = state.endpoints.filter(
          (item) => item.id !== action.payload.id
        );
        state.totalEndpoints = state.totalEndpoints - 1;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.services = state.services.filter(
          (item) => item.id !== action.payload.id
        );
        state.totalServices = state.totalServices - 1;
      })
      .addCase(createBackgroundTask.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.backgroundTasks = {
          ...state.backgroundTasks,
          total: state.backgroundTasks.total + 1,
          data: [...state.backgroundTasks.data, action.payload],
        };

        state.totalBackgroundTasks = state.totalBackgroundTasks + 1;
      })
      .addCase(deleteBackgroundTask.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedBackgroundTasks = state.backgroundTasks.data.filter(
          (item) => item.id !== action.payload.id
        );
        state.backgroundTasks = {
          ...state.backgroundTasks,
          total: state.backgroundTasks.total - 1,
          data: updatedBackgroundTasks,
        };
        state.totalBackgroundTasks = state.totalBackgroundTasks - 1;
      })
      .addCase(fetchDataModel.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedDataModels = state.dataModels.data.map((dataModel) =>
          dataModel.id === action.payload.id
            ? { ...dataModel, ...action.payload }
            : dataModel
        );
        state.dataModels = {
          ...state.dataModels,
          data: updatedDataModels,
          cloudService: action.payload.cloudService,
        };
        state.selectedDataModel = action.payload;
        if (action.payload.backgroundTasks) {
          state.totalBackgroundTasks = action.payload.backgroundTasks.length;
          state.totalCollections = action.payload.collections.length;
        }
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.collection = action.payload;
        state.properties = action.payload.schema.properties;
        const updatedCollections = state.collections.data.map((collection) =>
          collection.id === action.payload.id
            ? { ...collection, ...action.payload }
            : collection
        );
        state.collections = {
          ...state.collections,
          data: updatedCollections,
        };
      })
      .addCase(updateBackgroundTask.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedBackgroundTask = state.backgroundTasks.data.map(
          (backgroundtask) =>
            backgroundtask.id === action.payload.id
              ? { ...backgroundtask, ...action.payload }
              : backgroundtask
        );
        state.backgroundTasks = {
          ...state.backgroundTasks,
          data: updatedBackgroundTask,
        };
        state.selectedBackgroundTask = null;
      })
      .addCase(fetchSwaggerData.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.swaggerData = action.payload;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.service = action.payload;
      })
      .addCase(fetchPipelineStatus.fulfilled, (state, action) => {
        if (!action.payload) return;
        const isPipelineStatusFailed = action.payload.backend?.message;
        if (isPipelineStatusFailed) {
          const backend = action.payload.backend?.message
            ? {
                ...action.payload.backend,
                ...BACKEND_FAILURE_STATUS,
              }
            : action.payload.backend;
          state.pipelineStatus = { backend };
        } else state.pipelineStatus = action.payload;
      })
      .addCase(fetchReferenceCollectionsData.fulfilled, (state, action) => {
        state.referenceCollectionsData = uniqBy(
          [...state.referenceCollectionsData, action.payload],
          "id"
        );
      })
      .addCase(updateEndpoint.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.recentDeployedEndpointInfo = action.payload.deploymentInfo;
      })
      .addCase(fetchENVSecrets.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.secrets = action.payload;
      })
      .addCase(createSecret.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.secrets = [...state.secrets, action.payload];
      })
      .addCase(deleteSecret.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedSecrets = state.secrets.filter(
          (item) => item.id !== action.payload.id
        );
        state.secrets = {
          ...state.secrets,
          updatedSecrets,
        };
      })
      .addCase(updateSecretKey.fulfilled, (state, action) => {
        if (!action.payload) return;
        const updatedSecretKeys = state.secrets.map((secretKey) =>
          secretKey.id === action.payload.id
            ? { ...secretKey, ...action.payload }
            : secretKey
        );
        state.secrets = updatedSecretKeys;
        state.selectedSecretKey = null;
      })
      .addCase(fetchDataModelDeploymentsTrack.fulfilled, (state, action) => {
        if (action.payload) state.dataModelDeploymentsTrack = action.payload;
        
      })
      .addCase(fetchFilteredDataModels.fulfilled, (state, action) => {
        if (action.payload) state.filteredDataModels = action.payload;
      })
      .addCase(deployBEApp.fulfilled, (state, action) => {
        if (action.payload)
          state.pipelineStatus = { backend: BACKEND_INITIAL_STATUS };
        else state.pipelineStatus = { backend: BACKEND_FAILURE_STATUS };
      });
  },
});

export default slice;

export const { name, reducer, actions } = slice;
